import React, { useEffect, useState } from 'react';
import { Card, Col, Container, Modal, Row } from 'react-bootstrap';
import { Redirect, useHistory } from 'react-router';
import { toast } from 'sonner';
import CircularProgressBar from '../components/circular-progress';
import Logo from '../components/Logo';
import FormGenerator from '../form-generator/FormGenerator';
import { highlightError } from '../form-generator/helpers/utility';
import { makeRESTApiRequest } from '../helpers/api';
import { ENDPOINTS } from '../helpers/constants';
import { forgetPaswordForm, resetPasswordForm } from '../helpers/forms';
import { saveUserToLocal } from '../helpers/session';
import { isClient, isStaff } from '../helpers/global';
import LoginForm from '../components/login/LoginForm';

const redirectAfterAuth = ({ authResult, history }) => {
  saveUserToLocal(authResult, true);
  const role = authResult.role;
  history.push(isClient(role) ? '/org-dashboard' : isStaff(role) ? '/dashboard' : '/services/pipeline');
};

const OTPSent = ({ onResendClick, otpResetTimer, setOtpresetTimer }) => {
  useEffect(() => {
    let timer;
    if (otpResetTimer > 0) {
      timer = setTimeout(() => {
        setOtpresetTimer(prevTimerCount => prevTimerCount - 1);
      }, 1000);
    }
    return () => clearTimeout(timer);
  }, [otpResetTimer]);

  return (
    <>
      <div className="mt-1 text-right">
        {otpResetTimer > 0 ? (
          <i className="tinyFont text-muted mb-1">
            Please Wait{' '}
            <b className="text-dark">
              {otpResetTimer} {otpResetTimer > 1 ? 'Seconds' : 'second'}
            </b>{' '}
            to resend OTP.
          </i>
        ) : (
          <i
            className="a d-inline-block tinyFont text-primary mb-0 "
            onClick={() => {
              onResendClick();
              setOtpresetTimer(30);
            }}
          >
            Didn't receive an email? Tap here to resend
          </i>
        )}
      </div>
    </>
  );
};

const Login = () => {
  const [signInError, setSignInError] = useState(false);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [showForgotPassModal, setShowForgotPassModal] = useState(false);
  const [forgotPasswordSubmitting, setForgotPasswordSubmitting] = useState(false);
  const [resettingPasswordEmail, setResettingPasswordEmail] = useState(null);
  const [otpResetTimer, setOtpresetTimer] = useState(30);
  const [googleAuthError, setGoogleAuthError] = useState(null);

  const history = useHistory();

  // Handle Google OAuth callback
  useEffect(() => {
    const handleGoogleCallback = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const state = urlParams.get('state');

      if (code && state) {
        try {
          // Verify state matches stored session-uid
          const storedUid = localStorage.getItem('session-uid');
          if (state !== storedUid) {
            setSignInError('Invalid authentication state. Please try again.');
            // Clean URL
            window.history.replaceState({}, document.title, '/login');
            return;
          }

          const redirectUri = `${window.location.origin}/login`;

          setFormSubmitting(true);

          // Call backend to login with Google
          const { response: authResult, error } = await makeRESTApiRequest({
            endpoint: ENDPOINTS.LOGIN_WITH_GOOGLE,
            requestBody: { code, redirectUri }
          });

          setFormSubmitting(false);

          if (error) {
            setGoogleAuthError(error);

            // Clean URL
            window.history.replaceState({}, document.title, '/login');
            return;
          }

          redirectAfterAuth({ history, authResult });
        } catch (err) {
          setFormSubmitting(false);
          setGoogleAuthError('Google authentication failed. Please try again.');

          console.error(err);
          // Clean URL
          window.history.replaceState({}, document.title, '/login');
        }
      }
    };

    handleGoogleCallback();
  }, [history]);

  const loggedInEmail = localStorage.getItem('user-email');
  const role = localStorage.getItem('user-role');

  if (loggedInEmail && role) {
    return (
      <Redirect
        from="/login"
        to={isClient(role) ? '/org-dashboard' : isStaff(role) ? '/dashboard' : '/services/pipeline'}
      />
    );
  }

  const onLoginFormSubmit = async form => {
    setSignInError('');
    setFormSubmitting(true);

    const { email, password } = form;
    const requestBody = { email, password };
    try {
      const { response: authResult, error } = await makeRESTApiRequest({
        endpoint: ENDPOINTS.LOGIN,
        requestBody: requestBody
      });

      setFormSubmitting(false);
      if (error) {
        setSignInError(error);
        return;
      }

      redirectAfterAuth({ history, authResult });
    } catch (e) {
      setFormSubmitting(false);
      setSignInError('Something went wrong! Please try again');
      console.log(e);
    }
  };

  const onForgotPasswordFormSubmit = async form => {
    const { forgetPasswordEmail: email } = form;
    setForgotPasswordSubmitting(true);
    try {
      const { response, error } = await makeRESTApiRequest({
        endpoint: ENDPOINTS.FORGOT_PASSWORD,
        requestBody: { email }
      });
      setForgotPasswordSubmitting(false);
      if (error) {
        toast.error(error);
        return;
      }
      setResettingPasswordEmail(email);
    } catch (e) {
      setForgotPasswordSubmitting(false);
      toast.error('Something went wrong! Please try again');
      console.log(e);
    }
  };

  const onResetPasswordFormSubmit = async form => {
    const { otp, newPassword, confirmPassword } = form;
    if (newPassword !== confirmPassword) {
      highlightError(document.getElementById('confirmPassword'), 'Passwords do not match');
      return;
    }
    setForgotPasswordSubmitting(true);
    try {
      const { response: authResult, error } = await makeRESTApiRequest({
        endpoint: ENDPOINTS.RESET_PASSWORD,
        requestBody: { email: resettingPasswordEmail, newPassword, temporaryKey: otp }
      });
      setForgotPasswordSubmitting(false);
      if (error) {
        toast.error(error);
        return;
      }
      setResettingPasswordEmail(null);
      setShowForgotPassModal(false);
      toast.success('Password reset successfully! You can now login with your new password');
      history.push('/login');
    } catch (e) {
      setForgotPasswordSubmitting(false);
      toast.error('Something went wrong! Please try again');
      console.log(e);
    }
  };

  window['onResetPasswordFormSubmit'] = onResetPasswordFormSubmit;
  window['onForgotPasswordFormSubmit'] = onForgotPasswordFormSubmit;
  window['onLoginFormSubmit'] = onLoginFormSubmit;

  const onForgotPasswordModalHide = () => {
    setResettingPasswordEmail(false);
    setShowForgotPassModal(false);
  };

  const ForgotPasswordModal = () => {
    return (
      <Modal show={showForgotPassModal} onHide={onForgotPasswordModalHide} centered backdrop="static">
        <Modal.Header closeButton={!forgotPasswordSubmitting}>
          <Modal.Title>Forgot password</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!forgotPasswordSubmitting ? (
            <div className="fade-in">
              <FormGenerator prefix="fp" formJson={resettingPasswordEmail ? resetPasswordForm : forgetPaswordForm} />
              {resettingPasswordEmail && (
                <OTPSent
                  onResendClick={() => onForgotPasswordFormSubmit({ forgetPasswordEmail: resettingPasswordEmail })}
                />
              )}
            </div>
          ) : (
            <div className="p-5">
              <div className="p-5 text-center">
                <CircularProgressBar size={5} />
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>
    );
  };

  return (
    <Container fluid className="h-100">
      <Row className="h-100">
        <Col xs={1} className="bg-dark d-block"></Col>
        <Col
          xs={11}
          className="bg-light h-100 d-flex flex-column p-0"
          style={{ overflowY: 'auto', overflowX: 'hidden' }}
        >
          <Row className="justify-content-center flex-grow-1 py-2 px-0 px-md-5">
            <Col xs={10} md={8} lg={6} xl={5} className="align-self-center">
              <Card className="p-2 border-0 bg-transparent">
                <Card.Header className="p-0 mt-3 mb-2 bg-transparent border-0 text-center">
                  <div className="text-center">
                    <Logo />
                  </div>
                  <h6 className="text-muted mx-2 mt-4">
                    <div className="mb-2 font-weight-medium xlargeFont">
                      <span className="text-dark ">Hello!</span> Welcome to <b>Accurate Lien Search</b>. <br />
                    </div>
                    <b className="text-dark font-weight-medium largeFont">Sign in to continue</b>
                  </h6>
                </Card.Header>
                <Card.Body>
                  {ForgotPasswordModal()}
                  <LoginForm
                    onSubmit={onLoginFormSubmit}
                    formSubmitting={formSubmitting}
                    error={signInError}
                    googleAuthError={googleAuthError}
                    setGoogleAuthError={setGoogleAuthError}
                    onForgotPasswordClick={() => setShowForgotPassModal(true)}
                  />
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Col>
      </Row>
    </Container>
  );
};

export default Login;
